import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';

// Types
export interface DeprecatedAssets {
  youtube: boolean;
  pdf: boolean;
  [assetType: string]: boolean; // Allow for future asset type additions
}

/**
 * React hook to check for deprecated assets (YouTube and PDF)
 * @returns Object containing loading state, error state, and deprecated assets data
 */
export function useDeprecatedAssets() {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const { data, isLoading, error } = useQuery<DeprecatedAssets, Error>({
    queryKey: ['deprecatedAssets'],
    queryFn: async () => {
      // Check for YouTube assets
      const youtubeResults = await dataProvider.getList('editorial', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'DESC' },
        filter: { type: ['youtube'] },
      });

      // Check for PDF assets
      const pdfResults = await dataProvider.getList('editorial', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'DESC' },
        filter: { type: ['pdf'] },
      });

      // Use nullish coalescing to handle undefined values
      const hasYoutube = (youtubeResults?.total ?? 0) > 0;
      const hasPdf = (pdfResults?.total ?? 0) > 0;

      // Return the final result
      return {
        youtube: hasYoutube,
        pdf: hasPdf,
      } as DeprecatedAssets;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  return {
    loading: isLoading,
    error:
      error instanceof Error
        ? error
        : error
        ? new Error('Unknown error checking deprecated assets')
        : null,
    deprecatedAssets: data || null,
  };
}
